import React, { Component } from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import Hammer from "react-hammerjs";
import GalleryPrevious from "./GalleryPrevious";
import GalleryNext from "./GalleryNext";

import { withGlobalContext } from "../common/GlobalContext";

type Props = {
  classes: Object,
  className: string,
  compliance: Object,
  photos: Array<string>,
  retsId: string | null,
  CDN_IMAGE_SERVER_URL: string,
};

type State = {
  selectedPhoto: string | null,
  selectedPhotoIndex: number | null,
};

class Slideshow extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      selectedPhoto: props.selectedPhoto || null, // allow initially-selected photo to be passed in as a prop
      selectedPhotoIndex: props.selectedPhotoIndex || null,
    };
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleArrowKeys);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleArrowKeys);
  }

  handleSelectPhoto = (selectedPhoto, selectedPhotoIndex) => {
    this.setState({
      selectedPhoto,
      selectedPhotoIndex,
    });
  };

  // Handle Next Slideshow Image
  handleNext = (selectedPhotoIndex) => {
    if (!selectedPhotoIndex) {
      return;
    }
    const nextIndex = selectedPhotoIndex + 1;

    const { photos } = this.props;

    // bail if we reached the end
    if (nextIndex > photos.length) {
      return;
    }

    const nextPhoto = photos[nextIndex - 1];

    // focus thumbnail button when changed
    document.getElementById(`galleryBtn-${nextIndex}`).focus();

    return this.handleSelectPhoto(nextPhoto, nextIndex);
  };

  // Handle Previous Slideshow Image
  handlePrevious = (selectedPhotoIndex) => {
    if (!selectedPhotoIndex) {
      return;
    }
    const prevIndex = selectedPhotoIndex - 1;

    // bail if a user tries to go beyond the first image
    if (prevIndex < 1) {
      return;
    }

    const { photos } = this.props;

    const prevPhoto = photos[prevIndex - 1];

    // focus thumbnail button when changed
    document.getElementById(`galleryBtn-${prevIndex}`).focus();
    return this.handleSelectPhoto(prevPhoto, prevIndex);
  };

  // Handle Left oh Right Arrows
  handleArrowKeys = (e) => {
    const key = e.key;

    if (key === "ArrowLeft") {
      return this.handlePrevious(this.state.selectedPhotoIndex);
    } else if (key === "ArrowRight") {
      return this.handleNext(this.state.selectedPhotoIndex);
    }
  };

  // Handle Swipe on Slideshow
  handleSwipe(e) {
    if (e.direction === 4 && this.state.selectedPhotoIndex !== 1) {
      return this.handlePrevious(this.state.selectedPhotoIndex);
    } else if (
      e.direction === 2 &&
      this.state.selectedPhotoIndex !== this.props.photos.length
    ) {
      return this.handleNext(this.state.selectedPhotoIndex);
    }
  }

  render() {
    const {
      classes,
      className,
      compliance,
      photos,
      CDN_IMAGE_SERVER_URL,
      isV1,
    } = this.props;
    const watermarkQuery =
      compliance && compliance.photoWatermark
        ? `&mark=${compliance.photoWatermark}&markscale=${compliance.markScale}`
        : "";

    const { selectedPhoto } = this.state;

    let selectedPhotoPath;

    if (isV1) {
      const selectedPhotoBasePath = `${CDN_IMAGE_SERVER_URL}/${
        selectedPhoto.path
      }?`;
      selectedPhotoPath = selectedPhoto.hash
        ? `${selectedPhotoBasePath}hash=${selectedPhoto.hash}`
        : selectedPhotoBasePath;
    } else {
      selectedPhotoPath = selectedPhoto.hash
        ? `${selectedPhoto.path}hash=${selectedPhoto.hash}`
        : selectedPhoto.path;
    }

    return (
      <div id="slideshow" className={classNames(classes.root, className)}>
        <div className={classes.count}>
          {this.state.selectedPhotoIndex} of {photos.length}
        </div>
        <Hammer onSwipe={(e) => this.handleSwipe(e)}>
          <div
            className={classes.selectedPhoto}
            style={{
              backgroundImage: `url('${selectedPhotoPath}&auto=compress&fit=contain&w=1128&h=728&or=0${watermarkQuery}')`,
            }}
          />
        </Hammer>
        <div className={classes.thumbnails}>
          {photos.map((photo, i) => {
            let photoPath = "";

            if (isV1) {
              const photoBasePath = `${CDN_IMAGE_SERVER_URL}/${photo.path}?`;
              photoPath = photo.hash
                ? `${photoBasePath}hash=${photo.hash}`
                : photoBasePath;
            } else {
              photoPath = photo.hash
                ? `${photo.path}hash=${photo.hash}`
                : photo.path;
            }

            return (
              <button
                id={`galleryBtn-${i + 1}`}
                key={photo.id}
                type="button"
                className={classNames(
                  classes.thumbnail,
                  photo.id === this.state.selectedPhoto.id &&
                    classes.thumbnailSelected
                )}
                onClick={() => {
                  const selectedPhotoIndex = i + 1;
                  return this.handleSelectPhoto(photo, selectedPhotoIndex);
                }}
              >
                <img
                  id={`galleryImg-${i + 1}`}
                  className={classes.img}
                  srcSet={`
                    ${photoPath}&auto=format&fit=crop&w=280&h=180&or=0&dpr=1${watermarkQuery} 1x,
                    ${photoPath}&auto=format&fit=crop&w=280&h=180&or=0&dpr=2=${watermarkQuery} 2x,
                    ${photoPath}&auto=format&fit=crop&w=280&h=180&or=0&dpr=3${watermarkQuery} 3x,
                  `}
                  sizes="280px"
                  src={`${photoPath}&auto=format&fit=crop&w=280&h=180&or=0${watermarkQuery}`}
                  alt={`Slideshow thumbnail ${i + 1}`}
                />
              </button>
            );
          })}
        </div>
        {this.state.selectedPhotoIndex !== 1 && (
          <GalleryPrevious
            handlePrevious={() =>
              this.handlePrevious(this.state.selectedPhotoIndex)
            }
          />
        )}
        {this.state.selectedPhotoIndex !== photos.length && (
          <GalleryNext
            handleNext={() => this.handleNext(this.state.selectedPhotoIndex)}
          />
        )}
      </div>
    );
  }
}

export default withStyles((theme) => ({
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  selectedPhoto: {
    flex: "1",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    marginBottom: theme.spacing.unit * 0.75,
  },
  thumbnails: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "140px",
    overflowX: "auto",
  },
  thumbnail: {
    flexShrink: "0",
    display: "flex",
    margin: "2px",

    "&:focus": {
      outline: "2px solid #fff !important", // override blue focus ring with white solid ring
    },

    "& img": {
      maxWidth: "100%",
      maxHeight: "136px",
    },
  },
  thumbnailSelected: {
    outline: "2px solid #fff !important", // override blue focus ring with white solid ring
  },
  count: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "auto",
    right: "120px",
    padding: theme.spacing.unit * 0.5,
    background: "rgba(0,0,0,0.4)",
    borderBottomRightRadius: "4px",
    borderBottomLeftRadius: "4px",
    color: theme.colors.white,
    fontWeight: "600",
    fontSize: "15px",
    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
  },
}))(withGlobalContext(Slideshow));
